/**
 * Common Validators
 * Shared Zod schemas used across multiple validators
 */

import { z } from 'zod';
import { meetsPasswordPolicy, PASSWORD_REQUIREMENTS_MESSAGE } from '@/lib/password-policy';

// ============================================================================
// Sort Order
// ============================================================================

/**
 * Common sort order enum (asc/desc)
 * Used by: product, customer, order validators
 */
export const sortOrderEnum = z.enum(['asc', 'desc']);
export type SortOrder = z.infer<typeof sortOrderEnum>;

// ============================================================================
// Pagination
// ============================================================================

/**
 * Common pagination query parameters
 * Used in all list endpoints
 */
export const paginationQuerySchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
});

export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

// ============================================================================
// Email & Password (shared validation patterns)
// ============================================================================

/**
 * Email validation - lowercased and trimmed
 */
export const emailSchema = z
  .string()
  .email('Invalid email address')
  .max(255)
  .transform((val) => val.toLowerCase().trim());

/**
 * Password requirements: the Cognito pool policy (see `@/lib/password-policy`).
 */
export const passwordSchema = z
  .string()
  .max(256)
  .refine(meetsPasswordPolicy, PASSWORD_REQUIREMENTS_MESSAGE);

// ============================================================================
// Product Options (shared across cart and order)
// ============================================================================

/**
 * Selected option schema
 * Represents a customer's selection of a product option value
 * Used by: cart.validator, order.validator
 */
export const selectedOptionSchema = z.object({
  optionId: z.coerce.number().int().positive(),
  valueId: z.coerce.number().int().positive(),
});

/**
 * Extended selected option schema with display info
 * Used for order items where we store the option details at time of purchase
 */
export const selectedOptionWithDetailsSchema = selectedOptionSchema.extend({
  title: z.string().optional(),
  valueTitle: z.string().optional(),
  priceModifier: z.string().optional(),
  fulfillmentSku: z.string().nullable().optional(),
  // This option's share of the charged unit price (see SelectedOption).
  // Order-creation writers set it; request bodies never carry it.
  soldUnitPrice: z.string().regex(/^\d+\.\d{2}$/).optional(),
});

export type SelectedOptionInput = z.infer<typeof selectedOptionSchema>;
export type SelectedOptionWithDetails = z.infer<typeof selectedOptionWithDetailsSchema>;
